﻿import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../../store/uiSlice';
import { clsx } from 'clsx';

export const Header: React.FC = () => {
  const { isMobileMenuOpen, setMobileMenuOpen } = useUiStore();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const badgeRef = useRef<HTMLDivElement | null>(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    // 1. Initialize Badge Element
    const badge = document.createElement('div');
    badge.className = 'certik-emblem';
    badge.setAttribute('data-id', '432b018d');
    badge.innerHTML = `
      <a href="https://skynet.certik.com/projects/idngold?utm_source=SkyEmblem&utm_campaign=idngold&utm_medium=link" target="_blank" rel="noopener noreferrer">
           &nbsp;&nbsp;Project  Certik&nbsp;&nbsp;
      </a>
    `;
    badgeRef.current = badge;

    // 2. Initial Append (Default to Desktop)
    const desktopContainer = document.getElementById('certik-desktop-container');
    if (desktopContainer) {
      desktopContainer.appendChild(badge);
    }

    // 3. Load CertiK script
    const script = document.createElement('script');
    script.src = "https://emblem.certik-assets.com/script?pid=idngold&vid=432b018d";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (document.body.contains(script)) document.body.removeChild(script);
      if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
    };
  }, []);

  // Effect to handle reparenting based on mobile menu state
  useEffect(() => {
    const badge = badgeRef.current;
    const desktopContainer = document.getElementById('certik-desktop-container');
    const mobileContainer = document.getElementById('certik-mobile-container');

    if (!badge || !desktopContainer || !mobileContainer) return;

    if (isMobileMenuOpen) {
      mobileContainer.appendChild(badge);
    } else {
      desktopContainer.appendChild(badge);
    }
  }, [isMobileMenuOpen]);

  const toggleLanguage = async () => {
    const currentLang = i18n.language || 'en';
    const newLang = currentLang.startsWith('en') ? 'id' : 'en';
    console.log('Current language:', currentLang, '| Switching to:', newLang);

    try {
      await i18n.changeLanguage(newLang);
      localStorage.setItem('i18nextLng', newLang);
      console.log('Language successfully changed to:', i18n.language);
      window.dispatchEvent(new Event('languageChanged'));
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="site-header">
      <div className="container nav">
        {/* Logo */}
        <Link to="/" className="brand">
          <img
            src="/idngold_icon.png"
            alt="IDNGold Icon"
            className="brand-icon"
          />
          <img
            src="/idngold_text.png"
            alt="IDNGold"
            className="brand-text"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav-links">
          <Link
            to="/"
            className={clsx(isActive('/') && 'active')}
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/buy"
            className={clsx(isActive('/buy') && 'active')}
          >
            {t('nav.buy')}
          </Link>
          <Link
            to="/redemption"
            className={clsx(isActive('/redemption') && 'active')}
          >
            {t('nav.redemption')}
          </Link>
          <a
            href={`https://news.${window.location.hostname.replace('www.', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gold transition-colors"
          >
            {t('nav.news')}
          </a>
          <Link
            to="/roadmap"
            className={clsx(isActive('/roadmap') && 'active')}
          >
            Roadmap
          </Link>
          <Link
            to="/whitepaper"
            className={clsx(isActive('/whitepaper') && 'active')}
          >
            Whitepaper
          </Link>
          <Link
            to="/tokenomics"
            className={clsx(isActive('/tokenomics') && 'active')}
          >
            {t('nav.tokenomics')}
          </Link>

          {/* CertiK Emblem - Desktop Container */}
          <div
            id="certik-desktop-container"
            className="flex items-center transform scale-125 origin-right ml-12 mr-6"
          />

          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center w-10 h-10 rounded-md hover:bg-surface transition-colors"
            title={(i18n.language || 'en').startsWith('en') ? 'Switch to Bahasa Indonesia' : 'Switch to English'}
          >
            <img
              src={(i18n.language || 'en').startsWith('en') ? '/flag-id.svg' : '/flag-us.svg'}
              alt={(i18n.language || 'en').startsWith('en') ? 'Indonesia' : 'English'}
              className="w-6 h-6 rounded shadow-sm"
            />
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="btn btn-ghost nav-toggle md:hidden"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={clsx('container', !isMobileMenuOpen && 'hidden')}>
        <div className="md:hidden py-4 border-t border-border">
          <nav className="flex flex-col space-y-3">
            <Link
              to="/"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/buy"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/buy') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.buy')}
            </Link>
            <Link
              to="/redemption"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/redemption') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.redemption')}
            </Link>
            <a
              href={`https://news.${window.location.hostname.replace('www.', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold text-text-secondary"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.news')}
            </a>
            <Link
              to="/roadmap"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/roadmap') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              Roadmap
            </Link>
            <Link
              to="/whitepaper"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/whitepaper') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              Whitepaper
            </Link>
            <Link
              to="/tokenomics"
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors hover:text-gold',
                isActive('/tokenomics') ? 'text-gold bg-surface' : 'text-text-secondary'
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.tokenomics')}
            </Link>

            <button
              onClick={toggleLanguage}
              className="flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md text-text-secondary hover:text-gold transition-colors"
            >
              <img
                src={(i18n.language || 'en').startsWith('en') ? '/flag-id.svg' : '/flag-us.svg'}
                alt={(i18n.language || 'en').startsWith('en') ? 'Indonesia' : 'English'}
                className="w-6 h-6 rounded shadow-sm"
              />
              <span>{(i18n.language || 'en').startsWith('en') ? 'Bahasa Indonesia' : 'English'}</span>
            </button>

            {/* CertiK Emblem - Mobile Container */}
            <div
              id="certik-mobile-container"
              className="px-3 py-4 flex items-center justify-start transform scale-125 origin-left ml-2"
            />
          </nav>
        </div>
      </div>
    </header>
  );
};
